import React, { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { TOTP } from 'otpauth';
import { db } from '@root/firebase'; // Firestore instance
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '@context/AuthContext'; // Import the custom hook

const TwoMFA = ({ userId, email, password, onSwitchForm }) => {
  const { globalUser, login, logout } = useAuth(); // Destructure the `login` function from useAuth
  const logo = '/images/logo-full.png';

  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  console.log('🔍 Received userId in TwoMFA:', userId, email, password); // ✅ Debugging log

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    let newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length < 6) {
      toast.error('Please enter all 6 digits.');
      return;
    }

    try {
      // ✅ Fetch user's MFA secret from Firestore
      const mfaStorePath = `users/${userId.userId}/authentication/mfa`;
      const mfaDocRef = doc(db, mfaStorePath);
      console.log('🔍 Checking userId in TwoMFA:', userId);
      const userDoc = await getDoc(mfaDocRef);

      if (!userDoc.exists()) {
        toast.error('MFA setup not found.');
        return;
      }

      const userSecret = userDoc.data().mfaSecret;

      // ✅ Validate OTP
      const totp = new TOTP({
        secret: userSecret,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
      });

      const isValid = totp.validate({ token: enteredOtp, window: 1 });

      if (isValid !== null) {
        // ✅ OTP is correct, complete login
        await login(email.email, password.password); // Firebase auth will throw an error if incorrect
        toast.success('MFA verified! Logging in...');
        navigate('../../app/dashboard'); // Redirect to the dashboard
      } else {
        toast.error('Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']); // Reset OTP fields
        inputRefs.current[0]?.focus(); // Move focus to first input
      }
    } catch (error) {
      console.error('MFA verification failed:', error);
      toast.error('An error occurred. Try again.');
    }
  };

  return (
    <div className='authentication-bg min-vh-100'>
      <div className='container'>
        <div className='d-flex flex-column min-vh-100 px-3 pt-4'>
          <div className='row justify-content-center my-auto'>
            <div className='col-md-8 col-lg-6 col-xl-5'>
              <div className='card'>
                <div className='card-body p-4'>
                  <div className='text-center mt-2'>
                    <h5>Two-Factor Authentication</h5>
                    <p className='mb-4'>
                      Enter the 6-digit code from your authenticator app.
                    </p>
                  </div>

                  <div className='p-2 mt-3'>
                    <div className='row justify-content-center'>
                      {otp.map((digit, index) => (
                        <div key={index} className='col-2'>
                          <input
                            type='text'
                            className='form-control form-control-lg text-center two-step'
                            maxLength='1'
                            value={digit}
                            onChange={(e) =>
                              handleChange(index, e.target.value)
                            }
                            ref={(el) => (inputRefs.current[index] = el)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className='mt-3'>
                      <button
                        onClick={handleVerifyOTP}
                        className='btn btn-primary w-100'
                      >
                        Confirm
                      </button>
                    </div>

                    <div className='mt-4 text-center'>
                      <p className='mb-0'>
                        Forget it?{' '}
                        <a
                          href='#'
                          onClick={() => onSwitchForm('login')}
                          className='fw-medium text-primary'
                        >
                          Return to Login
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoMFA;
